import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { JobRole } from '@/lib/types';

export const teamKeys = { members: (jobId: string) => ['jobs', jobId, 'members'] as const };

export function useMembers(jobId: string) {
  return useQuery({ queryKey: teamKeys.members(jobId), queryFn: () => api.jobs.members(jobId) });
}

function useInvalidate(jobId: string) {
  const qc = useQueryClient();
  return () => Promise.all([qc.invalidateQueries({ queryKey: teamKeys.members(jobId) }), qc.invalidateQueries({ queryKey: ['jobs', 'detail', jobId] }), qc.invalidateQueries({ queryKey: ['jobs', 'list'] })]);
}

export function useInviteMember(jobId: string) {
  const invalidate = useInvalidate(jobId);
  return useMutation({ mutationFn: (body: { email: string; role: JobRole; message?: string }) => api.jobs.inviteMember(jobId, body), onSuccess: () => invalidate() });
}

export function useAddMember(jobId: string) {
  const invalidate = useInvalidate(jobId);
  return useMutation({ mutationFn: ({ userId, role }: { userId: string; role: JobRole }) => api.jobs.addMember(jobId, userId, role), onSuccess: () => invalidate() });
}

export function useUpdateMember(jobId: string) {
  const invalidate = useInvalidate(jobId);
  return useMutation({ mutationFn: ({ userId, role }: { userId: string; role: JobRole }) => api.jobs.updateMember(jobId, userId, role), onSuccess: () => invalidate() });
}

export function useRemoveMember(jobId: string) {
  const invalidate = useInvalidate(jobId);
  return useMutation({ mutationFn: (userId: string) => api.jobs.removeMember(jobId, userId), onSuccess: () => invalidate() });
}

export function useRevokeJobInvitation(jobId: string) {
  const invalidate = useInvalidate(jobId);
  return useMutation({ mutationFn: (id: string) => api.jobs.revokeInvitation(jobId, id), onSuccess: () => invalidate() });
}
